import {
  ApolloProvider as ApolloProviderApollo,
  ApolloClient,
  InMemoryCache,
  from,
  Observable,
  FetchResult,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
// @ts-ignore
import { createUploadLink } from 'apollo-upload-client';
import { auth } from '@/config/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { ReactNode, useMemo, useEffect, useContext } from 'react';
import { AuthContext, USER_KEY, TOKEN_KEY } from '@/context/auth';

type ApolloProviderProps = {
  children: ReactNode;
};

// Flag para evitar múltiplos refreshes simultâneos
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// Flag para evitar múltiplos refreshes iniciais (important for StrictMode)
let initialRefreshAttempted = false;

const refreshToken = async (): Promise<string> => {
  // Verifica se tem dados de autenticação salvos
  const savedToken = localStorage.getItem(TOKEN_KEY);
  const savedUser = localStorage.getItem(USER_KEY);

  if (!savedToken || !savedUser) {
    throw new Error('No authentication data found');
  }

  // Faz a chamada para o backend para obter um novo custom token
  const response = await fetch(`${import.meta.env.VITE_API_URL}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${JSON.parse(savedToken || '""')}`,
    },
    body: JSON.stringify({
      query: `
        query RefreshToken {
          refreshToken {
            access_token
          }
        }
      `,
    }),
  });

  const data = await response.json();

  if (data.errors) {
    throw new Error(data.errors[0]?.message || 'Failed to refresh token');
  }

  // Save user data before Firebase operation (Firebase might clear localStorage)
  const userDataBeforeAuth = localStorage.getItem(USER_KEY);

  // Faz login com o novo custom token
  await signInWithCustomToken(auth, data.data.refreshToken.access_token);
  const newIdToken = await auth.currentUser?.getIdToken();

  // Restore user data if Firebase cleared it
  if (userDataBeforeAuth && !localStorage.getItem(USER_KEY)) {
    localStorage.setItem(USER_KEY, userDataBeforeAuth);
  }

  if (!newIdToken) {
    throw new Error('Failed to get new ID token');
  }

  // Preserve existing user data during token refresh
  const existingUser = localStorage.getItem(USER_KEY);

  // Atualiza o token no localStorage
  localStorage.setItem(TOKEN_KEY, JSON.stringify(newIdToken));

  // Ensure user data is preserved (Firebase signInWithCustomToken might clear it)
  if (existingUser && !localStorage.getItem(USER_KEY)) {
    localStorage.setItem(USER_KEY, existingUser);
  }

  return newIdToken;
};

// Função para tentar refresh ao inicializar
const tryInitialRefresh = async (
  setAuthLoading: (loading: boolean) => void,
  clearAuth: () => void
): Promise<void> => {
  try {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);

    // Só tenta refresh se tem dados de autenticação
    if (savedToken && savedUser) {
      await refreshToken();
      // Token refresh successful, mark auth as loaded
      setAuthLoading(false);
    } else {
      // No auth data
      setAuthLoading(false);
    }
  } catch (error) {
    const err = error as Error;

    // Only clear auth if it's an authentication error, not a network error
    const isAuthError =
      err.message?.includes('UNAUTHENTICATED') ||
      err.message?.includes('token expired') ||
      err.message?.includes('invalid token') ||
      err.message?.includes('jwt expired');

    const isNetworkError =
      err.message?.includes('Load failed') ||
      err.message?.includes('fetch') ||
      err.message?.includes('HTTP');

    if (isAuthError && !isNetworkError) {
      clearAuth();
    }

    setAuthLoading(false);
  }
};

export const ApolloProvider = ({ children }: ApolloProviderProps) => {
  const authContext = useContext(AuthContext);

  // Tenta fazer refresh do token ao carregar a página - run only once with context coordination
  useEffect(() => {
    const attemptRefresh = () => {
      // Prevent multiple initial refresh attempts
      if (initialRefreshAttempted) {
        return;
      }

      const savedToken = localStorage.getItem(TOKEN_KEY);
      const savedUser = localStorage.getItem(USER_KEY);

      // Only attempt refresh if we have auth data and context is available
      if (savedToken && savedUser && authContext?.setAuthLoading && authContext?.clearAuth) {
        initialRefreshAttempted = true;
        tryInitialRefresh(authContext.setAuthLoading, authContext.clearAuth);
      } else if (savedToken && !authContext?.setAuthLoading) {
        // Context not ready yet, retry after a short delay
        setTimeout(attemptRefresh, 10);
      }
    };

    attemptRefresh();
  }, []); // Run only once

  const client = useMemo(() => {
    const { clearAuth } = authContext || {};

    // Get base API URL and ensure GraphQL endpoint
    const baseApiUrl = import.meta.env.VITE_API_URL || '';
    const graphqlUrl = `${baseApiUrl}/graphql`;

    // Helper function to convert HTTP URL to WebSocket URL
    const getWsUrl = (httpUrl: string): string => {
      return httpUrl.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
    };

    const wsUrl = getWsUrl(graphqlUrl);

    // WebSocket client for subscriptions
    const wsClient = createClient({
      url: wsUrl,
      connectionParams: () => {
        const savedToken = localStorage.getItem(TOKEN_KEY);
        const token = savedToken ? JSON.parse(savedToken) : null;

        const params = {
          authorization: token ? `Bearer ${token}` : '',
        };

        return params;
      },
      shouldRetry: () => true,
    });

    const wsLink = new GraphQLWsLink(wsClient);

    const uploadLink = createUploadLink({
      uri: graphqlUrl,
    });

    const authLink = setContext((_, { headers }) => {
      // Busca o token do localStorage dinamicamente em cada request
      const savedToken = localStorage.getItem(TOKEN_KEY);
      const token = savedToken ? JSON.parse(savedToken) : null;
      return {
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : '',
        },
      };
    });

    const errorLink = onError(({ graphQLErrors, operation, forward }) => {
      // Não tenta fazer refresh se já é uma operação de refresh
      if (operation.operationName === 'RefreshToken') {
        return;
      }

      // Verifica se tem token salvo
      const hasToken = localStorage.getItem(TOKEN_KEY);
      const hasUser = localStorage.getItem(USER_KEY);

      if (graphQLErrors) {
        for (const err of graphQLErrors) {
          // Verifica se o erro é relacionado a token inválido/expirado
          const isTokenError =
            err.extensions?.code === 'UNAUTHENTICATED' ||
            err.message?.toLowerCase().includes('token expired') ||
            err.message?.toLowerCase().includes('token inválido') ||
            err.message?.toLowerCase().includes('jwt expired') ||
            err.message?.toLowerCase().includes('jwt malformed') ||
            err.message?.toLowerCase().includes('invalid token');

          if (isTokenError) {
            // Se não tem dados de autenticação, não tenta refresh
            if (!hasToken || !hasUser) {
              return;
            }

            // Se já está fazendo refresh, reutiliza a promise existente
            if (isRefreshing && refreshPromise) {
              return new Observable<FetchResult>((observer) => {
                refreshPromise
                  ?.then((newToken) => {
                    operation.setContext({
                      headers: {
                        ...operation.getContext().headers,
                        authorization: `Bearer ${newToken}`,
                      },
                    });
                    forward(operation).subscribe(observer);
                  })
                  .catch((error) => {
                    observer.error(error);
                  });
              });
            }

            // Inicia um novo refresh
            isRefreshing = true;
            refreshPromise = refreshToken().finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });

            return new Observable<FetchResult>((observer) => {
              refreshPromise
                ?.then((newToken) => {
                  operation.setContext({
                    headers: {
                      ...operation.getContext().headers,
                      authorization: `Bearer ${newToken}`,
                    },
                  });
                  forward(operation).subscribe(observer);
                })
                .catch(() => {
                  // Clear auth state and let components handle redirect
                  if (clearAuth) {
                    clearAuth();
                  }
                  observer.error(err);
                });
            });
          }
        }
      }
    });

    // Split link to route subscriptions to WebSocket and queries/mutations to HTTP
    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
      },
      wsLink,
      from([errorLink, authLink, uploadLink])
    );

    return new ApolloClient({
      // @ts-ignore
      link: splitLink,
      cache: new InMemoryCache(),
      defaultOptions: {
        watchQuery: { fetchPolicy: 'network-only' },
        query: { fetchPolicy: 'network-only' },
      },
      uri: graphqlUrl,
    });
  }, [authContext?.clearAuth]);

  return <ApolloProviderApollo client={client}>{children}</ApolloProviderApollo>;
};
